import type { ScheduledSessionId } from '~/types/ids'
import { issue, type ValidationIssue, type ValidationIssues } from './common'
import type { PlannedItem } from './fitnessDomain'
import { isWeekday, type Weekday, weekdayOffset } from './weekday'

/**
 * The recurring training schedule (D-105).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  A WEEK WAS A QUOTA, AND THAT IS THE HOLE THIS CLOSES
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `PlannedItem` is `{label, timesPerWeek, minutes?}`. It says *three sessions a
 *  week* and cannot say *Monday's Upper Body session*, so there has never been
 *  anything for a user to mark done — which is why `dailyChecks.sessionDone` was
 *  written by nothing from C6.0 until this record (D-105 §1).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS IS THE PLAN LAYER, AND IT IS NOT THE EXECUTION LAYER
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  A `ScheduledSession` is **recurring**: it says a session happens on Mondays,
 *  not that a particular Monday's session happened. Completing, moving, skipping
 *  and adding a one-off all belong to the *materialised week* (D-105 §2, the
 *  `trainingWeeks` store) and **must never write back here**. Collapsing the two
 *  would move the target under a week already in progress, which is exactly what
 *  D-099 Amendment 2 forbids.
 *
 *  So there is no `outcome`, no `completedAt` and no date on anything in this
 *  file. If you find yourself wanting to add one, the thing you want is a
 *  materialised session.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  ONE AUTHORITY, AND ONE FUNCTION THAT DERIVES FROM IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  The schedule is the sole editable authority for the standard week, and
 *  `weeklyCommitment.standard` is derived read-only from it (D-105 §3).
 *  `standardWeekFor` is the single function every reader goes through.
 *  **Reimplementing that derivation per caller reintroduces the two-authority
 *  shape D-099 §D rejected**, without anyone deciding to.
 */

/**
 * One recurring session.
 *
 * **No time of day** (D-105 §9): a session is a plan commitment and supplies
 * Today's training prompt itself, where a routine anchor is an optional local
 * prompt window and is never evidence. Merging them would turn a preference into
 * evidence.
 *
 * `expectedMinutes` is **purely descriptive** (D-105 §10). No actual duration is
 * captured on completion and no rule may read it while `DOMAIN_REGISTRY` keeps
 * cardio, strength and mobility unimplemented — D-060's converse forbids
 * collecting a field nothing consumes, and an estimate the user typed to help
 * themselves plan is not the same as evidence.
 */
export interface ScheduledSession {
  /**
   * Stable across an edit.
   *
   * The id is what makes renaming Monday's session distinguishable from deleting
   * it and creating another (D-105 §7). A schedule editor that re-minted ids on
   * every save would make every edit look like a wholesale replacement in the
   * change history.
   */
  readonly id: ScheduledSessionId
  readonly weekday: Weekday
  readonly name: string
  readonly expectedMinutes?: number
}

/**
 * A domain's recurring week.
 *
 * **Duplicates are allowed**: two strength sessions on Monday is a legitimate
 * week and nothing may forbid it (D-105 §4). The array's order is not
 * meaningful across weekdays — `sessionsInWeekOrder` decides display order — but
 * it is preserved among sessions sharing one weekday, because that is the order
 * the user arranged them in.
 */
export type WeeklySchedule = readonly ScheduledSession[]

/**
 * The longest a session name may be.
 *
 * A display constraint rather than a coaching threshold, so it is **not** a
 * `rules/thresholds.ts` entry (D-105 §12) — it lives beside the validation that
 * applies it, exactly as `MAX_NOTE_LENGTH` does. The number is chosen so a name
 * survives a PDF table cell, a CSV column and the Today screen at 390 px
 * without wrapping to a third line.
 *
 * It is enforced here rather than only in the editor because the export
 * renderers read stored records, never the form: a name that reached storage by
 * any other path would still reach the PDF.
 */
export const MAX_SESSION_NAME_LENGTH = 60

/**
 * Sanity bounds for an expected duration.
 *
 * An entry-error guard, not an opinion about how long anyone should train — the
 * same reasoning as the circumference spread threshold's bounds. Sola has no
 * view on whether a 20-minute session is worthwhile, and D-105 §10 forbids one.
 */
const MIN_EXPECTED_MINUTES = 1
const MAX_EXPECTED_MINUTES = 24 * 60

export function validateWeeklySchedule(schedule: WeeklySchedule): ValidationIssues {
  const issues: ValidationIssue[] = []
  const seen = new Set<string>()

  for (const session of schedule) {
    issues.push(...identityIssues(session, seen))
    issues.push(...shapeIssues(session))
    seen.add(session.id)
  }

  return issues
}

/**
 * Split out rather than suppressed, which is the call D-056 asks for when the
 * complexity warning fires on code that decides whether a record may be stored.
 * Identity and shape are also the two questions that fail for different reasons:
 * a duplicate id is a bug in whatever built the schedule, and a bad name is
 * something the user typed.
 */
function identityIssues(session: ScheduledSession, seen: ReadonlySet<string>): ValidationIssues {
  if (session.id.trim().length === 0) {
    return [issue('weeklySchedule', 'Every scheduled session needs an identifier.')]
  }
  if (seen.has(session.id)) {
    /* Two sessions sharing an id are indistinguishable to every later edit, so
       one of them would silently absorb the other's changes. Two sessions on the
       same weekday with the same name are fine — it is the id that must be
       unique. */
    return [issue('weeklySchedule', 'Two scheduled sessions cannot share an identifier.')]
  }
  return []
}

function shapeIssues(session: ScheduledSession): ValidationIssues {
  const issues: ValidationIssue[] = []

  if (!isWeekday(session.weekday)) {
    issues.push(issue('weeklySchedule', 'A scheduled session must fall on a day of the week.'))
  }
  issues.push(...sessionShapeIssues('weeklySchedule', session.name, session.expectedMinutes))

  return issues
}

/**
 * The name and duration rules, shared by both layers.
 *
 * A materialised session carries the same two fields and must obey the same
 * bounds — a one-off added on Wednesday reaches the PDF, the CSV and the ZIP
 * manifest exactly as a recurring one does (D-105 §12). **Exported so the cap is
 * applied from one place rather than restated**, which is the same reasoning that
 * put `MAX_SESSION_NAME_LENGTH` in the model instead of beside the editor: a
 * second copy would be the one that drifts.
 */
export function sessionShapeIssues(
  field: string,
  name: string,
  expectedMinutes: number | undefined,
): ValidationIssues {
  const issues: ValidationIssue[] = []
  const trimmed = name.trim()

  if (trimmed.length === 0) {
    issues.push(issue(field, 'Every session needs a name.'))
  } else if (trimmed.length > MAX_SESSION_NAME_LENGTH) {
    issues.push(
      issue(field, `A session name may be at most ${MAX_SESSION_NAME_LENGTH} characters.`),
    )
  }
  if (!isRealisticDuration(expectedMinutes)) {
    issues.push(issue(field, 'An expected duration must be a realistic number of minutes.'))
  }

  return issues
}

/** Absent is valid: not every session has a duration worth stating. */
function isRealisticDuration(minutes: number | undefined): boolean {
  if (minutes === undefined) return true
  return (
    Number.isFinite(minutes) && minutes >= MIN_EXPECTED_MINUTES && minutes <= MAX_EXPECTED_MINUTES
  )
}

/**
 * The schedule laid out in the order the week runs.
 *
 * Chronological from the user's own week start, with sessions sharing a weekday
 * keeping the order they were arranged in. **Sorted with a stable comparison on
 * the offset alone** — comparing by raw weekday number would put Sunday first
 * for a Monday-start user, which is the bug this function exists to make
 * impossible to write twice.
 */
export function sessionsInWeekOrder(
  schedule: WeeklySchedule,
  weekStartDay: Weekday,
): readonly ScheduledSession[] {
  return [...schedule].sort(
    (left, right) =>
      weekdayOffset(left.weekday, weekStartDay) - weekdayOffset(right.weekday, weekStartDay),
  )
}

/**
 * The standard week, derived from the schedule.
 *
 * **One scheduled session becomes one `PlannedItem` with `timesPerWeek: 1`**,
 * preserving the name and the optional duration (D-105 §3). Three Monday
 * sessions are three items rather than one item happening three times, because
 * they are three separate things the user can complete, move or skip — and a
 * quota is precisely the representation this record exists to replace.
 *
 * The domain needs no field on the item: the schedule lives on the domain goal,
 * so the item's domain is whichever goal it was read from.
 */
export function deriveStandardWeek(
  schedule: WeeklySchedule,
  weekStartDay: Weekday,
): readonly PlannedItem[] {
  return sessionsInWeekOrder(schedule, weekStartDay).map((session) => ({
    label: session.name,
    timesPerWeek: 1,
    ...(session.expectedMinutes === undefined ? {} : { minutes: session.expectedMinutes }),
  }))
}

/**
 * The standard week for a goal, whichever layer holds it.
 *
 * **This is the single reader D-105 §3 requires, and it is the one function to
 * call.** A goal that has a schedule derives its standard week from it; a goal
 * that has not yet been given one keeps whatever `standard` it already had,
 * which stays valid historical data until the user creates a schedule.
 *
 * Deliberately takes the two fields rather than a `DomainGoalRecord`, so it can
 * be called from a snapshot, an export row or a review's frozen plan without
 * anyone reconstructing a record to satisfy the signature — and so this module
 * needs no runtime import from `fitnessDomain.ts`.
 */
export function standardWeekFor(
  goal: {
    readonly weeklySchedule?: WeeklySchedule
    readonly weeklyCommitment?: { readonly standard: readonly PlannedItem[] }
  },
  weekStartDay: Weekday,
): readonly PlannedItem[] {
  if (goal.weeklySchedule !== undefined && goal.weeklySchedule.length > 0) {
    return deriveStandardWeek(goal.weeklySchedule, weekStartDay)
  }
  return goal.weeklyCommitment?.standard ?? []
}
